import {
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import set from 'lodash/fp/set'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'
import { v4 as uuid } from 'uuid'

import { PaginatedTable } from 'components/PaginatedTable'
import { makeMapping } from 'configuration/dataSources/helpers'
import { DATES, RANGE_LAG } from 'const/filters'
import { DEFAULT_TABLE_WIDGET_PAGE_SIZE } from 'const/tables'
import { WIDGET_GRID_Y_MARGIN, WIDGET_GRID_ROW_HEIGHT } from 'const/widgets'
import GroupByTooltip from 'features/MetricsGlossary/components/GroupByTooltip'
import MetricTooltip from 'features/MetricsGlossary/components/MetricTooltip'
import {
    useGetDataSourceGroupBysList,
    useGetDataSourceMetricsList,
} from 'features/MetricsGlossary/queries'
import { withCurrencyLocale } from 'helpers/formatting'
import {
    getResponsiveTablePaginationOptions,
    PaginationSize,
} from 'helpers/pagination'
import {
    deserializeColumns,
    serializeColumns,
} from 'helpers/tables/serialization'
import { shortNameToNode } from 'helpers/typeGuard'
import { isGroupedByDate } from 'helpers/ui/dashboardPage'
import type {
    CellRenderProps,
    DateRangeFilter,
    Field,
    Pagination,
    PeriodDeltaType,
    Sorter,
    Widget as IWidget,
} from 'types'

import * as styles from './styles.scss'
import type { TableWidgetBaseProps } from './types'

function calculatePaginationSize(width: number): PaginationSize {
    if (width < 500) {
        return 'xs'
    }
    if (width < 700) {
        return 'sm'
    }
    return 'default'
}

const HEADER_BASE_HEIGHT = 70
const TITLE_HEIGHT = 31
const DESCRIPTION_HEIGHT = 16
const PAGINATION_ROW_HEIGHT = 36

export function TableWidgetBase({
    widget,
    isLoading,
    widgetData,
    totalCount,
    pageNumber,
    onPageNumberChange,
    filters,
    dataSource,
    metricsColumns,
    groupByColumns,
    onColumnsChange,
    onSorterChange,
    onPaginationChange,
    currencyCode,
    reportedRange,
    reportedPriorRange,
}: TableWidgetBaseProps): ReactElement {
    const tableWidgetWrapper = useRef<HTMLDivElement>(null)

    const [tableHeaderHeight, setTableHeaderHeight] = useState(0)

    const [localColumnOverrides, setLocalColumnOverrides] = useState<{
        metrics: IWidget['metrics']
        group_by: IWidget['group_by']
    } | null>(null)
    const [localSorter, setLocalSorter] = useState<Sorter | null>(null)
    const [localPagination, setLocalPagination] = useState<Pagination | null>(
        null
    )
    const [localPageNumber, setLocalPageNumber] = useState<number>(
        pageNumber ?? 1
    )

    useEffect(() => {
        setLocalColumnOverrides(null)
        setLocalSorter(null)
        setLocalPagination(null)
        setLocalPageNumber(pageNumber ?? 1)
    }, [pageNumber, widget.id])

    useEffect(() => {
        if (onColumnsChange || !localColumnOverrides) {
            return
        }
        if (
            isEqual(localColumnOverrides.metrics, widget.metrics) &&
            isEqual(localColumnOverrides.group_by, widget.group_by)
        ) {
            setLocalColumnOverrides(null)
        }
    }, [localColumnOverrides, onColumnsChange, widget.group_by, widget.metrics])

    const { data: groupByListData } = useGetDataSourceGroupBysList(
        dataSource?.slug
    )
    const { data: metricListData } = useGetDataSourceMetricsList(
        dataSource?.slug
    )
    const [paginationSize, setPaginationSize] = useState(
        calculatePaginationSize(tableWidgetWrapper.current?.clientWidth ?? 0)
    )

    const handleResize = useCallback((): void => {
        const node = tableWidgetWrapper.current
        if (node) {
            setPaginationSize(calculatePaginationSize(node.clientWidth))
        }
    }, [])

    useEffect(() => {
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [handleResize])

    const {
        sorter,
        group_by,
        metrics,
        include_change_metrics,
        layout,
        name,
        description,
        period_delta_type,
        period_delta_date_range,
    } = widget

    const shouldUseLocalColumns = !onColumnsChange
    const shouldUseLocalSorter = !onSorterChange
    const shouldUseLocalPagination = !onPaginationChange
    const shouldUseLocalPageNumber = !onPageNumberChange

    const effectiveMetrics = shouldUseLocalColumns
        ? (localColumnOverrides?.metrics ?? metrics)
        : metrics
    const effectiveGroupBy = shouldUseLocalColumns
        ? (localColumnOverrides?.group_by ?? group_by)
        : group_by

    const effectiveSorter = shouldUseLocalSorter
        ? (localSorter ?? sorter ?? {})
        : (sorter ?? {})

    const effectivePageNumber = shouldUseLocalPageNumber
        ? localPageNumber
        : (pageNumber ?? 1)

    const pageSize =
        (shouldUseLocalPagination
            ? (localPagination?.pageSize ?? widget.pagination?.pageSize)
            : widget.pagination?.pageSize) ?? DEFAULT_TABLE_WIDGET_PAGE_SIZE

    const data = widgetData ?? []

    const groupByColumnsTransformed = useMemo(() => {
        const cols = (
            groupByColumns
                ? deserializeColumns<any>(
                      effectiveGroupBy,
                      groupByColumns,
                      false
                  )
                : []
        ).map((each) => {
            const matchingGroupBy =
                each.groupBy ??
                groupByListData?.data?.results.find((m) =>
                    m.metadata_all_names.includes(each.id)
                )

            return {
                ...each,
                antTableColumnOptions: {
                    ...each.antTableColumnOptions,
                    title: matchingGroupBy ? (
                        <GroupByTooltip groupBy={matchingGroupBy}>
                            <div
                                className={
                                    styles['dashboard-table-header-title']
                                }
                            >
                                {shortNameToNode(each.shortName)}
                            </div>
                        </GroupByTooltip>
                    ) : (
                        <div className={styles['dashboard-table-header-title']}>
                            {typeof each.antTableColumnOptions?.title ===
                            'function'
                                ? each.antTableColumnOptions?.title({})
                                : each.antTableColumnOptions?.title}
                        </div>
                    ),
                    className: styles['title-with-tooltip'],
                },
                data: undefined,
            }
        })

        // fixed the first visible column to left
        const firstVisibleIndex = cols.findIndex(
            (col) => col.isVisible !== false
        )
        if (firstVisibleIndex !== -1) {
            cols[firstVisibleIndex] = set(
                ['antTableColumnOptions', 'fixed'],
                'left'
            )(cols[firstVisibleIndex])
        }
        return cols
    }, [effectiveGroupBy, groupByColumns, groupByListData?.data?.results])

    const metricsColumnsTransformed = useMemo(() => {
        const glossaryMetrics = metricListData?.data?.results ?? []
        let cols = (
            metricsColumns
                ? deserializeColumns<any>(
                      effectiveMetrics,
                      metricsColumns,
                      false
                  )
                : []
        ).map((each) => {
            const matchingMetric =
                each.metric ??
                glossaryMetrics.find((m) => m.metadata_key === each.id)

            return {
                ...each,
                antTableColumnOptions: {
                    ...each.antTableColumnOptions,
                    title:
                        matchingMetric && dataSource ? (
                            <MetricTooltip
                                metric={matchingMetric}
                                dataSource={dataSource}
                                placement="top"
                            >
                                <div
                                    className={
                                        styles['dashboard-table-header-title']
                                    }
                                >
                                    {shortNameToNode(each.shortName)}
                                </div>
                            </MetricTooltip>
                        ) : (
                            <div
                                className={
                                    styles['dashboard-table-header-title']
                                }
                            >
                                {typeof each.antTableColumnOptions?.title ===
                                'function'
                                    ? each.antTableColumnOptions?.title({})
                                    : each.antTableColumnOptions?.title}
                            </div>
                        ),
                    className: styles['title-with-tooltip'],
                },
                data: undefined,
            }
        })

        // hide totals for last value metrics when grouped by date
        if (isGroupedByDate(effectiveGroupBy.map((each) => each?.id ?? each))) {
            cols = cols.map((each) =>
                each.metricOptions?.isLastValue
                    ? {
                          ...each,
                          isTotalSupported: false,
                      }
                    : each
            )
        }

        return cols
    }, [
        dataSource,
        effectiveGroupBy,
        effectiveMetrics,
        metricListData?.data?.results,
        metricsColumns,
    ])

    const paginationHeight =
        paginationSize === 'xs'
            ? 2 * PAGINATION_ROW_HEIGHT
            : PAGINATION_ROW_HEIGHT

    const headerHeight =
        HEADER_BASE_HEIGHT +
        (name.length > 0 ? TITLE_HEIGHT : 0) +
        (description.length > 0 ? DESCRIPTION_HEIGHT : 0)

    const approxWidgetHeight =
        layout.h * WIDGET_GRID_ROW_HEIGHT +
        (layout.h - 1) * WIDGET_GRID_Y_MARGIN

    const approxContentHeight =
        headerHeight +
        (widget.show_filter_row ? 30 : 0) +
        tableHeaderHeight +
        paginationHeight

    const scroll = useMemo(() => {
        return {
            y: approxWidgetHeight - approxContentHeight,
        }
    }, [approxWidgetHeight, approxContentHeight])

    // Metric cell renderers format currency via a numeral format string whose
    // `$` resolves against numeral's global locale (the dashboard currency).
    // Market Analysis values arrive in the segment marketplace's native
    // currency, so scope each cell render to the widget's resolved currencyCode.
    // No-op when currencyCode is unset (every other data source).
    const metricsColumnsWithCurrency = useMemo(() => {
        if (!currencyCode) {
            return metricsColumnsTransformed
        }
        const scopeRender = <
            H extends { render: (props: CellRenderProps<any>) => ReactNode },
        >(
            holder: H
        ): H => ({
            ...holder,
            render: (props: CellRenderProps<any>) =>
                withCurrencyLocale(currencyCode, () => holder.render(props)),
        })
        const scopeField = <
            T extends {
                metricOptions?: {
                    render: (props: CellRenderProps<any>) => ReactNode
                }
                renderOptions?: {
                    render: (props: CellRenderProps<any>) => ReactNode
                }
                priorPeriodOptions?: {
                    render: (props: CellRenderProps<any>) => ReactNode
                }
            },
        >(
            field: T
        ): T => ({
            ...field,
            ...(field.metricOptions && {
                metricOptions: scopeRender(field.metricOptions),
            }),
            ...(field.renderOptions && {
                renderOptions: scopeRender(field.renderOptions),
            }),
            ...(field.priorPeriodOptions && {
                priorPeriodOptions: scopeRender(field.priorPeriodOptions),
            }),
        })
        return metricsColumnsTransformed.map((col) => ({
            ...scopeField(col),
            childrenFields: col.childrenFields?.map(scopeField),
        }))
    }, [metricsColumnsTransformed, currencyCode])

    const allColumns = useMemo(
        () => groupByColumnsTransformed.concat(metricsColumnsWithCurrency),
        [groupByColumnsTransformed, metricsColumnsWithCurrency]
    )

    useEffect(() => {
        handleResize()
    }, [layout.h, layout.w, tableHeaderHeight, handleResize])

    const handleUpdateColumns = useCallback(
        (columns: Field<any>[]): void => {
            const mapping = makeMapping(
                serializeColumns(columns) as Field<any>[]
            )
            const nextMetrics = effectiveMetrics.map((each) => {
                const key = typeof each === 'string' ? each : each.id
                const mapped = mapping[key]
                return mapped ?? { id: key }
            })
            const nextGroupBy = effectiveGroupBy.map((each) => {
                const key = typeof each === 'string' ? each : each.id
                const mapped = mapping[key]
                return mapped ?? { id: key }
            })

            if (onColumnsChange) {
                onColumnsChange({ metrics: nextMetrics, group_by: nextGroupBy })
                return
            }

            setLocalColumnOverrides({
                metrics: nextMetrics,
                group_by: nextGroupBy,
            })
        },
        [effectiveGroupBy, effectiveMetrics, onColumnsChange]
    )

    const handleUpdatePagination = useCallback(
        (newPagination: Pagination): void => {
            if (onPaginationChange) {
                onPaginationChange(newPagination)
            } else {
                setLocalPagination(newPagination)
            }

            const nextPage = newPagination.current ?? 1
            if (onPageNumberChange) {
                if (nextPage !== effectivePageNumber) {
                    onPageNumberChange(nextPage)
                }
            } else if (nextPage !== localPageNumber) {
                setLocalPageNumber(nextPage)
            }
        },
        [
            effectivePageNumber,
            localPageNumber,
            onPageNumberChange,
            onPaginationChange,
        ]
    )

    const handleUpdateSorter = useCallback(
        (newSorter: Sorter): void => {
            if (onSorterChange) {
                onSorterChange(newSorter)
            } else {
                setLocalSorter(newSorter)
            }

            // Keep table behavior predictable: sorting always starts from page 1.
            if (onPageNumberChange) {
                if (effectivePageNumber !== 1) {
                    onPageNumberChange(1)
                }
            } else if (localPageNumber !== 1) {
                setLocalPageNumber(1)
            }
        },
        [
            effectivePageNumber,
            localPageNumber,
            onPageNumberChange,
            onSorterChange,
        ]
    )

    return (
        <div ref={tableWidgetWrapper}>
            <PaginatedTable
                readonly
                shouldMonitorTime={false} // loading time is monitored in the parent <Widget /> component
                rowKey="id"
                columns={allColumns}
                dataSource={(data ?? []).map((record) => ({
                    id: uuid(),
                    ...record,
                }))}
                loading={!!isLoading}
                pagination={{
                    pageSize,
                    total: totalCount,
                    current: effectivePageNumber,
                    ...getResponsiveTablePaginationOptions({ paginationSize }),
                    position: ['bottomCenter'],
                }}
                scroll={scroll}
                updateColumns={handleUpdateColumns}
                sorter={effectiveSorter}
                updatePagination={handleUpdatePagination}
                updateSorter={handleUpdateSorter}
                updateRecord={noop}
                reloadData={noop}
                showPeriodDeltas={include_change_metrics}
                showTotalRow={widget?.show_totals ?? false}
                periodDeltaType={period_delta_type as PeriodDeltaType}
                filterDates={filters[DATES] as DateRangeFilter}
                periodDeltaDateRange={period_delta_date_range}
                rangeLag={filters[RANGE_LAG] as unknown as number}
                reportedRange={reportedRange}
                reportedPriorRange={reportedPriorRange}
                onHeaderRow={() => ({
                    className: styles['dashboard-table-header'],
                })}
                onHeaderHeightChange={setTableHeaderHeight}
            />
        </div>
    )
}
